import { useEffect, useState } from 'react';
import { config } from '../config';

export interface AppMeta {
  app_id: string;
  slug: string;
  name: string;
  logo_url: string | null;
  primary_color: string | null;
  sdk_version: string | null;
  active: boolean;
}

export interface AppMetaError {
  code: 'app.not_found' | 'app.network' | 'app.server_error';
  status?: number;
}

export interface UseAppMetaState {
  data?: AppMeta;
  error?: AppMetaError;
  loading: boolean;
}

interface CacheEntry {
  data: AppMeta;
  fetchedAt: number;
}

const CACHE_TTL_MS = 60_000;
const cache = new Map<string, CacheEntry>();

/** Test helper — exportado só pra tests/unit resetarem cache entre specs. */
export function __clearMetaCache() {
  cache.clear();
}

export function useAppMeta(slug: string): UseAppMetaState {
  const [state, setState] = useState<UseAppMetaState>(() => {
    const cached = cache.get(slug);
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      return { data: cached.data, loading: false };
    }
    return { loading: true };
  });

  useEffect(() => {
    const cached = cache.get(slug);
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      // Slug change that hits cache — sync to cached data (first-mount hit
      // already handled by useState initializer; this covers re-renders with
      // different slug prop).
      setState({ loading: false, data: cached.data });
      return;
    }

    // Cache miss — reset to loading state (important pra slug changes em runtime;
    // sem isso consumer ve stale data do slug anterior ate fetch completar).
    setState({ loading: true });

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${config.apiHost}/api/apps/meta/${slug}`);
        if (cancelled) return;
        if (res.status === 404) {
          setState({ loading: false, error: { code: 'app.not_found', status: 404 } });
          return;
        }
        if (!res.ok) {
          setState({
            loading: false,
            error: { code: 'app.server_error', status: res.status },
          });
          return;
        }
        const data = (await res.json()) as AppMeta;
        if (cancelled) return;
        cache.set(slug, { data, fetchedAt: Date.now() });
        setState({ loading: false, data });
      } catch {
        if (!cancelled) {
          setState({ loading: false, error: { code: 'app.network' } });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return state;
}
